<template>
  <div class="full-height">
    <div class="position--relative">
      <transition name="fade">
        <v-progress-linear
          v-if="isLoading"
          class="progress-linear--absolute"
          color="warning"
          height="3"
          indeterminate
        />
      </transition>
    </div>

    <transition name="fade">
      <div v-if="isSuccess" class="full-height">
        <div v-if="items.length" class="table">
          <div class="table__status">
            <strong>{{ firstItemNumber }}</strong>-<strong>{{ lastItemNumber }}</strong> {{ $t('Common.of') }} <strong>{{ pagination.totalElements | formatNumber }}</strong>&nbsp;<span class="text--lowercase">{{ $t('Common.employees') }}</span>
          </div>

          <v-layout class="table__header" align-center>
            <v-flex
              v-if="
                claims.company['ROLE_DELETE_COMPANY_ID_EMPLOYEES'] ||
                claims.employee['ROLE_PUT_EMPLOYEE_ID'] ||
                claims.employee['ROLE_POST_EMPLOYEE_ID_RESENDWELCOMEEMAIL'] ||
                claims.employee['ROLE_PUT_EMPLOYEE_ID_ADDTOALLCOSTCENTERS'] ||
                claims.employee['ROLE_DELETE_EMPLOYEE_ID']
              "
              class="table__checkbox"
              xs1
            >
              <v-checkbox
                v-if="
                  claims.company['ROLE_DELETE_COMPANY_ID_EMPLOYEES'] &&
                  items && items.length
                "
                v-model="checkedAllItems"
                :disabled="!selectedCompany"
                hide-details
                @change="handleCheckedAllItems"
              />
            </v-flex>
            <v-flex xs1>ID</v-flex>
            <v-flex xs2>{{ $t('Common.employee') | capitalizeFirstLetter  }}</v-flex>
            <v-flex xs2>{{ $t('Common.company') | capitalizeFirstLetter  }}</v-flex>
            <v-flex xs2>{{ $t('Common.phone') | capitalizeFirstLetter  }}</v-flex>
            <v-flex xs2>{{ $t('Common.email') | capitalizeFirstLetter  }}</v-flex>
            <v-flex xs2>CPF</v-flex>
          </v-layout>

          <v-list subheader two-line class="table__body">
            <div v-for="(item, i) in items" :key="`tr-${i}`">
              <v-list-tile
                class="table__body__tr"
                :class="{ 'table__body__tr--active': activateLine(item) }"
                @click="handleClick($event, item)"
              >
                <v-list-tile-content>
                  <v-list-tile-sub-title>
                    <v-layout align-center>
                      <v-flex
                        v-if="
                          claims.company['ROLE_DELETE_COMPANY_ID_EMPLOYEES'] ||
                          claims.employee['ROLE_PUT_EMPLOYEE_ID'] ||
                          claims.employee['ROLE_POST_EMPLOYEE_ID_RESENDWELCOMEEMAIL'] ||
                          claims.employee['ROLE_PUT_EMPLOYEE_ID_ADDTOALLCOSTCENTERS'] ||
                          claims.employee['ROLE_DELETE_EMPLOYEE_ID']
                        "
                        class="table__checkbox table__td--prevent"
                        xs1
                      >
                        <v-checkbox
                          v-if="claims.company['ROLE_DELETE_COMPANY_ID_EMPLOYEES']"
                          v-model="selectedItems"
                          :value="item.id"
                          :disabled="!selectedCompany"
                          hide-details
                          @change="handleSelectedItems"
                        />

                        <v-menu
                          v-if="
                            claims.employee['ROLE_PUT_EMPLOYEE_ID'] ||
                            claims.employee['ROLE_POST_EMPLOYEE_ID_RESENDWELCOMEEMAIL'] ||
                            claims.employee['ROLE_PUT_EMPLOYEE_ID_ADDTOALLCOSTCENTERS'] ||
                            claims.employee['ROLE_DELETE_EMPLOYEE_ID']
                          "
                          offset-y
                          right
                        >
                          <button class="table-body__actions" slot="activator">
                            <v-icon>
                              more_vertical
                            </v-icon>
                          </button>

                          <v-list class="dropdown-menu-actions__list">
                            <v-list-tile
                              v-if="claims.employee['ROLE_PUT_EMPLOYEE_ID']"
                              @click="$router.push({ name: 'Employee', params: { type: 'editar', id: item.id } })"
                            >
                              <v-list-tile-title>{{$t(`${namespace}.editEmployee`)}}</v-list-tile-title>
                            </v-list-tile>

                            <v-list-tile
                              v-if="claims.employee['ROLE_POST_EMPLOYEE_ID_RESENDWELCOMEEMAIL']"
                              @click="handleResendWelcomeEmail({
                                id: item.id,
                                name: item.name
                              })"
                            >
                              <v-list-tile-title>{{$t(`${namespace}.sendEmail`)}}</v-list-tile-title>
                            </v-list-tile>

                            <v-list-tile
                              v-if="claims.employee['ROLE_PUT_EMPLOYEE_ID_ADDTOALLCOSTCENTERS']"
                              @click="handleAddToAllCostCenters(item.id)"
                            >
                              <v-list-tile-title>{{ $t(`${namespace}.addToAllCostCenters`) }}</v-list-tile-title>
                            </v-list-tile>

                            <v-divider
                              v-if="
                                (
                                  claims.employee['ROLE_PUT_EMPLOYEE_ID'] ||
                                  claims.employee['ROLE_POST_EMPLOYEE_ID_RESENDWELCOMEEMAIL'] ||
                                  claims.employee['ROLE_PUT_EMPLOYEE_ID_ADDTOALLCOSTCENTERS']
                                ) &&
                                claims.employee['ROLE_DELETE_EMPLOYEE_ID']
                              "
                            />

                            <v-list-tile
                              v-if="claims.employee['ROLE_DELETE_EMPLOYEE_ID']"
                              @click="handleSingleDeleteDialog(item)"
                            >
                              <v-list-tile-title>{{$t(`${namespace}.deleteEmployee`)}}</v-list-tile-title>
                            </v-list-tile>
                          </v-list>
                        </v-menu>
                      </v-flex>

                      <v-flex xs1>
                        <span :title="item.id" class="text--ellipsis" contenteditable>
                          {{ item.id }}
                        </span>
                      </v-flex>

                      <v-flex xs2>
                        <span :title="item.name" class="text--ellipsis">
                          {{ item.name | ellipsis(25) }}
                        </span>
                      </v-flex>

                      <v-flex xs2 class="text--ellipsis">
                        <span :title="item.company.name" class="text--ellipsis">
                          {{ item.company.name | ellipsis(25) }}
                        </span>
                      </v-flex>

                      <v-flex xs2>
                        <span v-if="item.phone" :title="item.phone | phoneWithDDD" class="text--ellipsis">{{ item.phone | phoneWithDDD }}</span>
                        <span v-else-if="!claims.employee['ROLE_PUT_EMPLOYEE_ID']">-</span>
                        <router-link
                          v-else
                          :to="{ name: 'Employee', params: { type: 'editar', id: item.id } }"
                          :title="$t('Common.setPhone')"
                          class="text--link accent--text"
                        >
                          {{ $t('Common.setPhone') }}
                        </router-link>
                      </v-flex>

                      <v-flex xs2>
                        <span v-if="item.email" :title="item.email" class="text--ellipsis">{{ item.email }}</span>
                        <span v-else-if="!claims.employee['ROLE_PUT_EMPLOYEE_ID']">-</span>
                        <router-link
                          v-else
                          :to="{ name: 'Employee', params: { type: 'editar', id: item.id } }"
                          :title="$t('Common.setEmail')"
                          class="text--link accent--text"
                        >
                          {{ $t('Common.setEmail') }}
                        </router-link>
                      </v-flex>

                      <v-flex xs2>
                        <span v-if="item.nationalId" :title="item.nationalId | formatCPF" class="text--ellipsis">{{ item.nationalId | formatCPF }}</span>
                        <span v-else-if="!claims.employee['ROLE_PUT_EMPLOYEE_ID']">-</span>
                        <router-link
                          v-else
                          :to="{ name: 'Employee', params: { type: 'editar', id: item.id } }"
                          :title="$t('Common.setCPF')"
                          class="text--link accent--text"
                        >
                          {{ $t('Common.setCPF') }}
                        </router-link>
                      </v-flex>
                    </v-layout>
                  </v-list-tile-sub-title>
                </v-list-tile-content>
              </v-list-tile>

              <v-divider />
            </div>
          </v-list>
        </div>

        <EmptyState
          v-else
          image="cost-center"
          :text="$t(`${namespace}.noneEmployeeFound`)"
        />

        <div v-if="pagination.totalElements > pagination.itemsPerPage" class="text-xs-center">
          <v-container class="px-0">
            <v-pagination
              v-model="pagination.currentPage"
              color="accent"
              :total-visible="totalVisible"
              :length="pagination.totalPages"
              @input="handlePage"
            />
          </v-container>
        </div>
      </div>
    </transition>

    <transition name="fade">
      <v-layout v-if="isFailed" align-center justify-center column fill-height>
        {{ $t('Common.genericErrorMsg') }}<br>
        <a
          :title="$t('Common.tryAgain')"
          class="accent--text"
          @click.prevent="handleReloadData"
        >
          {{ $t('Common.tryAgain') }}.
        </a>
      </v-layout>
    </transition>

    <GlobalDrawer
      :show="globalDrawer.visibility"
      :drawerType="globalDrawer.type"
      :data="globalDrawer.data"
      :filters="globalDrawer.filters"
      @close="closeGlobalDrawer"
    />
  </div>
</template>

<script>
import { mapState, mapActions } from 'vuex'
import { STATUS_IS_LOADING, STATUS_SUCCESS, STATUS_FAILED } from 'utils/constants'
import { isArray, formatNumber } from 'utils/functions'
import EmptyState from 'components/EmptyState'
import GlobalDrawer from 'components/globalDrawer'

export default {
  name: 'Table',
  props: {
    items: {
      type: Array,
      required: true
    },
    pagination: {
      type: Object,
      required: true
    },
    status: {
      type: Number,
      default: STATUS_IS_LOADING
    },
    clearSelectedItems: {
      type: Boolean
    },
    drawerVisibility: {
      type: Boolean
    }
  },
  components: {
    EmptyState,
    GlobalDrawer
  },
  computed: {
    ...mapState({
      selectedCompany: ({ company }) => company.selectedCompany,
      claims: ({ userClaims }) => userClaims.claims
    }),

    routeId () {
      const { id } = this.$route.params
      return id ? Number(id) : null
    },

    totalVisible () {
      return window.innerWidth <= 420 ? 5 : 7
    },

    firstItemNumber () {
      const { currentPage, itemsPerPage } = this.pagination
      const calc = (currentPage * itemsPerPage) - itemsPerPage + 1
      const item = currentPage === 1 ? 1 : calc
      return formatNumber(item)
    },

    lastItemNumber () {
      const { currentPage, itemsPerPage, totalElements } = this.pagination
      const calc = currentPage * itemsPerPage
      const item = calc > totalElements ? totalElements : calc
      return formatNumber(item)
    },

    isLoading () {
      return this.status === STATUS_IS_LOADING
    },

    isSuccess () {
      return this.status === STATUS_SUCCESS
    },

    isFailed () {
      return this.status === STATUS_FAILED
    }
  },
  data () {
    return {
      namespace: 'Views.EmployeesPage',
      selectedItem: null,
      checkedAllItems: false,
      selectedItems: [],
      globalDrawer: {
        visibility: false,
        type: '',
        data: {},
        filters: {}
      }
    }
  },
  methods: {
    ...mapActions({
      showAlert: 'snackbar/show'
    }),

    isValidArray (val) {
      return isArray(val)
    },

    openGlobalDrawer ({ data = {}, filters = {} }) {
      this.globalDrawer = {
        visibility: true,
        type: 'employee',
        data,
        filters: {
          ...this.filters,
          employeeId: data.id
        }
      }
    },

    closeGlobalDrawer (reload = null) {
      this.globalDrawer = {
        visibility: false,
        type: '',
        data: {},
        filters: {}
      }

      this.selectedItem = null
      reload && this.$emit('reloadData')
    },

    handlePage (page) {
      this.closeGlobalDrawer()
      this.$emit('goToPage', page)
    },

    handleReloadData () {
      this.$emit('reloadData')
    },

    activateLine (item) {
      return item.id === this.selectedItem || this.selectedItems.find(i => i === item.id)
    },

    handleClick ($event, item) {
      const cls = $event.target.classList

      if (
        !cls.contains('v-btn__content') &&
        !cls.contains('v-icon') &&
        !cls.contains('v-input--selection-controls__ripple') &&
        !cls.contains('accent--text')
      ) {
        this.selectedItem = item.id
        this.openGlobalDrawer({
          data: {
            id: item.id,
            name: item.name
          }
        })
      }
    },

    openDrawerIfIdExist () {
      if (this.routeId) {
        this.openGlobalDrawer({
          data: {
            id: this.routeId
          }
        })

        this.selectedItem = this.routeId
      }
    },

    handleSingleDeleteDialog (item) {
      this.$emit('deleteItem', item)
    },

    handleCheckedAllItems (val) {
      this.selectedItems = val
        ? [...this.items].map(item => item.id)
        : []
    },

    handleSelectedItems (val) {
      this.checkedAllItems = this.selectedItems.length === this.items.length
    },

    async handleResendWelcomeEmail ({ id, name }) {
      try {
        await this.$apiService.resendWelcomeEmail(id)
        this.showAlert({
          type: 'success',
          message: `${this.$t(`${this.namespace}.sucessResendEmail`)} ${name}`
        })
      } catch (err) {
        console.log('Error', err)
        this.showAlert({
          type: 'error',
          message: this.$t(`${this.namespace}.errorResendEmail`)
        })
      }
    },

    async handleAddToAllCostCenters (employeeId) {
      try {
        await this.$apiService.addToAllCostCenters(employeeId)
        this.showAlert({
          type: 'success',
          message: this.$t(`${this.namespace}.addToAllCostCentersSuccess`)
        })
        this.closeGlobalDrawer()
      } catch (err) {
        this.showAlert({
          type: 'error',
          message: this.$t(`${this.namespace}.addToAllCostCentersError`)
        })
        console.log('Error', err.response)
      }
    }
  },
  watch: {
    selectedCompany () {
      this.closeGlobalDrawer()
    },

    selectedItems (val) {
      this.$emit('selectedItems', val)
    },

    clearSelectedItems () {
      this.selectedItems = []
      this.checkedAllItems = false
    },

    drawerVisibility () {
      this.closeGlobalDrawer()
    }
  },
  mounted () {
    this.openDrawerIfIdExist()
  }
}
</script>
